import { exists } from '@/helpers/utilities';
import SliderNav from '@/components/SliderNav';

let HeroSlideshow = (() => {
    class HeroSlideshow {
        constructor(selector) {
            this.node = document.querySelector(selector);
            this.nodeExists = exists(this.node);
            if (!this.nodeExists) return;

            this.slides = Array.from(this.node.querySelectorAll('[data-js="slide"]'));
            this.bgSlides = Array.from(this.node.parentNode.querySelectorAll('[data-js="image-slide"]'));
            this.nav = new SliderNav(document.querySelector(`[data-js="hero-slider-controls"]`), this.slides.length);

            this.playbtn = this.node.querySelector(`[data-js="play"]`);
            this.pauseBtn = this.node.querySelector(`[data-js="pause"]`);

            //slideshow state and settings
            this.mode = this.viewportWidth > 1024 ? this.desktop : this.mobile;
            this.step = 0;
            this.nextStep = null;
            this.prevStep = null;
            this.duration = 7600;
            this.isPlaying = true;
            this.clickToPause = false;
            this.hasSetup = false;

            document.documentElement.style.setProperty('--heroSlideshowDuration', `${this.duration}ms`);
        }

        setBgVisibility(step) {
            this.bgSlides.map((slide) => {
                slide.classList.add('js-is-hidden');
            });

            if (!exists(this.bgSlides[step])) return;
            this.bgSlides[step].classList.remove('js-is-hidden');
        }

        setVisibility(step) {
            this.slides.map((slide) => {
                slide.classList.add('js-is-hidden');
            });

            if (!exists(this.slides[step])) return;
            this.slides[step].classList.remove('js-is-hidden');
        }

        setStep(step) {
            this.step = step;
            this.nextStep = this.step < this.slides.length - 1 ? this.step + 1 : 0;
            this.prevStep = this.step > 0 ? this.step - 1 : this.slides.length - 1;

            requestAnimationFrame(() => {
                this.setBgVisibility(step);
                this.setVisibility(step);
                this.nav.update(step);

                if (window.innerWidth < 1024) return;
                this.resetSlideShowTimer();
            });
        }

        debounceClick() {
            this.justClicked = true;

            setTimeout(() => {
                this.justClicked = false;
            }, 300);
        }

        onDotClick(e) {
            let selectedStep = parseInt(e.currentTarget.dataset.step);
            if (selectedStep == this.step) return;
            this.setStep(selectedStep);
        }

        onPlayPauseClick(e) {
            if (this.justClicked) return;
            this.debounceClick();

            if (this.isPlaying) {
                this.pauseSlideshow();
                this.clickToPause = true;
            } else {
                this.resumeSlideshow();
                e.currentTarget.blur();
                this.clickToPause = false;
            }
        }

        advanceSlideshow() {
            this.setStep(this.nextStep);
            window.dispatchEvent(new CustomEvent('reflowGrid'));
        }

        toggleButtonState() {
            this.playbtn.classList.toggle('js-is-active');
            this.pauseBtn.classList.toggle('js-is-active');
        }

        pauseSlideshow() {
            if (!this.isPlaying) return;
            this.isPlaying = false;
            this.clearSlideshowTimer();
            this.toggleButtonState();
        }

        resumeSlideshow() {
            if (this.isPlaying) return;
            this.isPlaying = true;
            this.advanceSlideshow();
            this.resetSlideShowTimer();
            this.toggleButtonState();
        }

        resetSlideShowTimer() {
            this.clearSlideshowTimer();
            this.runSlideshow();
        }

        clearSlideshowTimer() {
            clearInterval(this.slideshowInterval); //just stops the interval function...
            this.slideshowInterval = null;
            /*... so we need to manually set the interval variable
            to a falsy condition in order to check interval state */
        }

        checkFocus() {
            let hasFocus = this.node.contains(this.node.querySelector(':focus-visible'));

            if (hasFocus) {
                if (!this.slideshowInterval) return;
                this.pauseSlideshow();
            } else {
                if (this.slideshowInterval || this.clickToPause) return;
                this.resumeSlideshow();
            }
        }

        runSlideshow() {
            if (this.slideshowInterval) return;
            //raise this timer up in the scope so it can be cancelled if needed
            this.slideshowInterval = setInterval(this.advanceSlideshow.bind(this), this.duration);
        }

        runFocusLoop() {
            setInterval(this.checkFocus.bind(this), 500);
        }

        setEventBindings() {
            [...this.nav.dots].map((dot) => {
                dot.addEventListener('click', this.onDotClick.bind(this));
            });

            [this.playbtn, this.pauseBtn].map((btn) => {
                btn.addEventListener('click', this.onPlayPauseClick.bind(this));
            });
        }
        createScreenObserver() {
            let intersectionObserver = new IntersectionObserver((entries) => {
                if (entries[0].intersectionRatio <= 0) {
                    this.clearSlideshowTimer();
                } else {
                    this.runSlideshow();
                }
            });

            intersectionObserver.observe(this.node);
        }

        onWindowResize() {
            if (window.innerWidth < 1024) return this.destroySlideshow();
            this.doSetup();
        }

        setWindowEventBindings() {
            window.addEventListener('resize', this.onWindowResize.bind(this));
        }

        destroySlideshow() {
            if (!this.hasSetup) return;
            this.hasSetup = false;
            this.setStep(0);
            this.clearSlideshowTimer(); 

            return false;
        }

        doSetup() {
            if (this.hasSetup) return;
            this.hasSetup = true;
            //always start at 0
            this.setStep(0);
            this.runSlideshow();
            this.runFocusLoop();
        }

        init() {
            if (!this.nodeExists) return;
            this.setWindowEventBindings();
            this.setEventBindings();
            if (window.innerWidth < 1024) return;

            this.createScreenObserver();
            this.doSetup();
        }
    }

    return {
        init({ selector }) {
            return new HeroSlideshow(selector).init();
        },
    };
})();

export default Object.create(HeroSlideshow);
